import {SentimentType} from "../../definitions/enums";
import {RatingReviewMediaModel} from "./ratingReviewMediaModel";
import {RatingReviewMediaType, RatingReviewType} from "../../definitions/types";
import React from "react";

export class RatingReviewModel {
    private readonly _id: number;
    private readonly _ratingId: number;
    private readonly _title?: string;
    private readonly _text: string;
    private readonly _sentiment?: SentimentType;
    private readonly _helpfulCount: number;
    private readonly _visible: boolean;
    private readonly _createdOn: Date;
    private readonly _media: RatingReviewMediaModel[];

    constructor(public readonly ratingReviewType: RatingReviewType) {
        this._id = ratingReviewType.id;
        this._ratingId = ratingReviewType.ratingId;
        this._title = ratingReviewType.title;
        this._text = ratingReviewType.text;
        this._sentiment = ratingReviewType.sentiment;
        this._helpfulCount = ratingReviewType.helpfulCount;
        this._visible = ratingReviewType.visible;
        this._createdOn = ratingReviewType.created;
        this._media = ratingReviewType.media
            .filter((ratingReviewMedia): ratingReviewMedia is RatingReviewMediaType => ratingReviewMedia !== null)
            .map((ratingReviewMedia) => new RatingReviewMediaModel(ratingReviewMedia));
    }

    /**
     * @method id
     * @description Returns the id of the review within the rating.
     * @returns {number}
     */
    public get id(): number {
        return this._id;
    }

    /**
     * @method ratingId
     * @description Returns the id of the rating the review belongs to.
     * @returns {number}
     */
    public get ratingId(): number {
        return this._ratingId;
    }

    /**
     * @method title
     * @description Returns the title of the review.
     * @returns {string | undefined}
     */
    public get title(): string | undefined {
        return this._title;
    }

    /**
     * @method text
     * @description Returns the text of the review.
     * @returns {string}
     */
    public get text(): string {
        return this._text;
    }

    /**
     * @method sentiment
     * @description Returns the sentiment of the review, if it has been analyzed. (Positive, Neutral, Negative, Mixed)
     * @returns {SentimentType | undefined}
     */
    public get sentiment(): SentimentType | undefined {
        return this._sentiment;
    }

    /**
     * @method helpfulCount
     * @description Returns the number of users who found the review helpful.
     * @returns {number}
     */
    public get helpfulCount(): number {
        return this._helpfulCount;
    }

    /**
     * @method visible
     * @description Returns whether the review is visible to the public.
     * @returns {boolean}
     */
    public get visible(): boolean {
        return this._visible;
    }

    /**
     * @method createdOn
     * @description Returns the date the review was created in UTC.
     * @returns {Date}
     */
    public get createdOn(): Date {
        return this._createdOn;
    }

    /**
     * @method media
     * @description Returns the media associated with the review, if any.
     * @returns {RatingReviewMediaModel[]}
     */
    public get media(): RatingReviewMediaModel[] {
        return this._media;
    }

    /**
     * @method hasMedia
     * @description Returns whether the review has any media associated with it.
     * @returns {boolean}
     */
    public get hasMedia(): boolean {
        return this._media.length > 0;
    }

    /**
     * @method visibleMedia
     * @description Returns the visible media associated with the review, if any.
     * @returns {RatingReviewMediaModel[]}
     */
    public get visibleMedia(): RatingReviewMediaModel[] {
        return this._media.filter((media) => media.visible);
    }

    /**
     * @method hasVisibleMedia
     * @description Returns whether the review has any visible media associated with it.
     * @returns {boolean}
     */
    public get hasVisibleMedia(): boolean {
        return this._media.some((media) => media.visible);
    }

    /**
     * @method isHelpful
     * @description Returns whether the review has been marked as helpful.
     * @returns {boolean}
     */
    public get isHelpful(): boolean {
        return this._helpfulCount > 0;
    }
}